// Hybrid Canvas 2D + WebGL2 starfield with tight diffraction spikes
// - Canvas 2D renders square pixel stars with bright Atom One Dark colors
// - WebGL2 post-processing applies tight diffraction spikes
// - DPR aware, zero per-frame allocations, adaptive to resize

import {
  StarburstSimple,
  type StarburstSimpleParams,
} from "./starburst-simple";

export type StarfieldOptions = {
  starCount?: number;
  maxSpeed?: number;
  tint?: [number, number, number];
  background?: string;
  mouseParallax?: boolean;
  grain?: boolean;
  starburst?: StarburstSimpleParams;
};

type Star = {
  x: number;
  y: number;
  z: number;
  s: number;
  a: number;
  vx: number;
  vy: number;
  color: [number, number, number];
  speedMult: number;
  age: number;
  fadeInDuration: number;
  shiny: boolean;
  glintPhase: number;
};

export type StarfieldHandle = {
  start(): void;
  stop(): void;
  destroy(): void;
  setTint(rgb: [number, number, number]): void;
};

const ATOM_ONE_DARK_COLORS: Array<[number, number, number]> = [
  [224, 108, 117],
  [209, 154, 102],
  [229, 192, 123],
  [152, 195, 121],
  [86, 182, 194],
  [97, 175, 239],
  [198, 120, 221],
  [190, 80, 70],
];

function getCanvasDimensions(canvas: HTMLCanvasElement) {
  const cssW = canvas.clientWidth || window.innerWidth;
  const cssH = canvas.clientHeight || window.innerHeight;
  const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
  const width = Math.max(1, Math.floor(cssW * dpr));
  const height = Math.max(1, Math.floor(cssH * dpr));
  return { width, height, dpr, cx: width * 0.5, cy: height * 0.5 };
}

function rotateHue(
  rgb: [number, number, number],
  degrees: number,
): [number, number, number] {
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / delta + 2) / 6;
    else h = ((r - g) / delta + 4) / 6;
  }

  h = (h + degrees / 360) % 1;
  const s = max === 0 ? 0 : delta / max;
  const v = max;

  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  let rOut = 0;
  let gOut = 0;
  let bOut = 0;

  switch (i % 6) {
    case 0:
      rOut = v;
      gOut = t;
      bOut = p;
      break;
    case 1:
      rOut = q;
      gOut = v;
      bOut = p;
      break;
    case 2:
      rOut = p;
      gOut = v;
      bOut = t;
      break;
    case 3:
      rOut = p;
      gOut = q;
      bOut = v;
      break;
    case 4:
      rOut = t;
      gOut = p;
      bOut = v;
      break;
    case 5:
      rOut = v;
      gOut = p;
      bOut = q;
      break;
  }

  return [
    Math.round(rOut * 255),
    Math.round(gOut * 255),
    Math.round(bOut * 255),
  ];
}

function renderStar(
  ctx: CanvasRenderingContext2D,
  star: Star,
  parallaxX: number,
  parallaxY: number,
  timeSeconds: number,
) {
  const px = parallaxX * (0.2 + star.z * 0.8);
  const py = parallaxY * (0.2 + star.z * 0.8);

  const fadeProgress = Math.min(1, star.age / star.fadeInDuration);
  let currentAlpha = star.a * fadeProgress;

  let renderColor = star.color;
  if (star.shiny) {
    const hueRotation = (timeSeconds * 30 + star.glintPhase * 57) % 360;
    renderColor = rotateHue(star.color, hueRotation);

    const glintFreq = 0.8;
    const glintPhase = star.glintPhase + timeSeconds * glintFreq * Math.PI * 2;
    const glintWave = Math.sin(glintPhase);
    if (glintWave > 0.7) {
      const glintBoost = (glintWave - 0.7) / 0.3;
      currentAlpha = Math.min(1.0, currentAlpha * (1.0 + glintBoost * 0.8));
    }
  }

  ctx.fillStyle = `rgb(${renderColor[0]}, ${renderColor[1]}, ${renderColor[2]})`;
  ctx.globalAlpha = currentAlpha;
  ctx.fillRect(star.x + px, star.y + py, star.s, star.s);
}

function createSourceTexture(gl: WebGL2RenderingContext): WebGLTexture {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    1,
    1,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    null,
  );
  return tex;
}

function initWebGLPostProcessor(
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
): {
  gl: WebGL2RenderingContext;
  starburst: StarburstSimple;
  srcTexture: WebGLTexture;
} {
  const gl = canvas.getContext("webgl2", {
    alpha: false,
    antialias: false,
    premultipliedAlpha: false,
  })!;
  if (!gl) {
    throw new Error("WebGL2 not supported");
  }

  const starburst = new StarburstSimple(gl, width, height);
  const srcTexture = createSourceTexture(gl);

  return { gl, starburst, srcTexture };
}

function updateAndRenderStars(
  ctx: CanvasRenderingContext2D,
  stars: Star[],
  dt: number,
  baseSpeed: number,
  parallaxX: number,
  parallaxY: number,
  W: number,
  H: number,
  CX: number,
  CY: number,
  dpr: number,
  timeSeconds: number,
) {
  for (let i = 0; i < stars.length; i++) {
    const st = stars[i]!;

    st.age += dt;

    const speed = baseSpeed * st.speedMult;
    st.x += st.vx * speed * dt;
    st.y += st.vy * speed * dt;
    st.speedMult *= 1.0 + Math.random() * 0.01;

    if (st.x < -50 || st.x > W + 50 || st.y < -50 || st.y > H + 50) {
      respawnStar(st, W, H, CX, CY, dpr);
      continue;
    }

    renderStar(ctx, st, parallaxX, parallaxY, timeSeconds);
  }
}

function applyStarburstPostProcess(
  gl: WebGL2RenderingContext,
  starburst: StarburstSimple | null,
  srcTexture: WebGLTexture | null,
  offscreen: HTMLCanvasElement,
  W: number,
  H: number,
  timeSeconds: number,
  params: StarburstSimpleParams,
) {
  if (!starburst || !srcTexture) return;

  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.bindTexture(gl.TEXTURE_2D, srcTexture);
  gl.texSubImage2D(
    gl.TEXTURE_2D,
    0,
    0,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    offscreen,
  );

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, W, H);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.BLEND);
  starburst.run(srcTexture, timeSeconds, params);
}

function respawnStar(
  star: Star,
  W: number,
  H: number,
  CX: number,
  CY: number,
  dpr: number,
): void {
  const x = Math.random() * W;
  const y = Math.random() * H;

  const dx = x - CX;
  const dy = y - CY;
  const distFromCenter = Math.hypot(dx, dy);
  const maxDist = Math.hypot(W / 2, H / 2);
  const normalizedDist = Math.min(1, distFromCenter / maxDist);

  const proximityToCenter = 1 - normalizedDist;
  const z = Math.random();
  let s = (0.5 + proximityToCenter * 1.5 + Math.random() * 0.8) * dpr * 1.2;

  let a = 0.3 + proximityToCenter * 0.5 + Math.random() * 0.2;

  const isShiny = Math.random() < 0.12;
  if (isShiny) {
    s *= 1.8;
    a = Math.min(1.0, a * 1.5);
  }

  const len = Math.hypot(dx, dy) || 1;
  const vx = dx / len;
  const vy = dy / len;

  const colorIndex = Math.floor(Math.random() * ATOM_ONE_DARK_COLORS.length);
  const color = ATOM_ONE_DARK_COLORS[colorIndex]!;

  star.x = x;
  star.y = y;
  star.z = z;
  star.s = s;
  star.a = a;
  star.vx = vx;
  star.vy = vy;
  star.color = color;
  star.speedMult = 0.2 + Math.random() * 0.8;
  star.age = 0;
  star.fadeInDuration = 0.3 + Math.random() * 0.7;
  star.shiny = isShiny;
  star.glintPhase = Math.random() * Math.PI * 2;
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: Complex initialization with WebGL + Canvas 2D hybrid architecture
export function initPrettyStarfield(
  canvas: HTMLCanvasElement,
  opts: StarfieldOptions = {},
): StarfieldHandle {
  const cfg = {
    starCount: opts.starCount ?? 700,
    maxSpeed: opts.maxSpeed ?? 30,
    tint: opts.tint ?? [100, 220, 255],
    background: opts.background ?? "#020409",
    mouseParallax: opts.mouseParallax ?? true,
    grain: opts.grain ?? true,
    starburst: opts.starburst ?? {
      intensity: 0.9,
      threshold: 0.22,
      pixelSnap: true,
      chromaticAberration: 0.0,
      grainEnabled: true,
      grainIntensity: 0.03,
      paletteMode: true,
      ditherEnabled: true,
    },
  };

  const offscreen = document.createElement("canvas");
  const ctx = offscreen.getContext("2d", { alpha: true })!;

  const dims = getCanvasDimensions(canvas);
  const webgl = initWebGLPostProcessor(canvas, dims.width, dims.height);
  const gl = webgl.gl;
  let starburst: StarburstSimple | null = webgl.starburst;
  let srcTexture: WebGLTexture | null = webgl.srcTexture;

  let running = false;
  let RAF = 0;
  let dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
  let W = 0;
  let H = 0;
  let CX = 0;
  let CY = 0;
  let lastTS = performance.now();
  const stars: Star[] = new Array(cfg.starCount);
  let parallaxX = 0;
  let parallaxY = 0;
  let targetPX = 0;
  let targetPY = 0;

  function resize() {
    const dims = getCanvasDimensions(canvas);
    dpr = dims.dpr;

    canvas.width = dims.width;
    canvas.height = dims.height;

    offscreen.width = dims.width;
    offscreen.height = dims.height;

    W = dims.width;
    H = dims.height;
    CX = dims.cx;
    CY = dims.cy;
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    if (!srcTexture) srcTexture = createSourceTexture(gl);
    gl.bindTexture(gl.TEXTURE_2D, srcTexture);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      dims.width,
      dims.height,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      null,
    );

    if (starburst) {
      starburst.resize(dims.width, dims.height);
    } else {
      starburst = new StarburstSimple(gl, dims.width, dims.height);
    }
  }

  function initStars() {
    for (let i = 0; i < stars.length; i++) {
      stars[i] = {
        x: 0,
        y: 0,
        z: 0,
        s: 0,
        a: 0,
        vx: 0,
        vy: 0,
        color: [255, 255, 255],
        speedMult: 0.3,
        age: 0,
        fadeInDuration: 1.0,
        shiny: false,
        glintPhase: 0,
      };
      respawnStar(stars[i]!, W, H, CX, CY, dpr);
    }
  }

  function onMouse(e: MouseEvent) {
    if (!cfg.mouseParallax) return;
    const rect = canvas.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    targetPX = nx * 24 * dpr;
    targetPY = ny * 24 * dpr;
  }

  function frame(ts: number) {
    if (!running) return;
    RAF = requestAnimationFrame(frame);
    const dt = Math.min(0.05, (ts - lastTS) / 1000);
    lastTS = ts;

    parallaxX += (targetPX - parallaxX) * 0.06;
    parallaxY += (targetPY - parallaxY) * 0.06;

    ctx.fillStyle = cfg.background;
    ctx.fillRect(0, 0, W, H);

    ctx.save();
    ctx.globalCompositeOperation = "lighter";

    const baseSpeed = cfg.maxSpeed * dpr;
    const timeSeconds = ts * 0.001;
    updateAndRenderStars(
      ctx,
      stars,
      dt,
      baseSpeed,
      parallaxX,
      parallaxY,
      W,
      H,
      CX,
      CY,
      dpr,
      timeSeconds,
    );

    ctx.restore();

    applyStarburstPostProcess(
      gl,
      starburst,
      srcTexture,
      offscreen,
      W,
      H,
      ts * 0.001,
      cfg.starburst,
    );
  }

  function start() {
    if (running) return;
    running = true;
    lastTS = performance.now();
    RAF = requestAnimationFrame(frame);
  }

  function stop() {
    running = false;
    if (RAF) cancelAnimationFrame(RAF);
    RAF = 0;
  }

  function destroy() {
    stop();
    window.removeEventListener("resize", resize);
    window.removeEventListener("mousemove", onMouse);

    if (starburst) {
      starburst.dispose();
      starburst = null;
    }
    if (srcTexture) {
      gl.deleteTexture(srcTexture);
      srcTexture = null;
    }

    ctx.clearRect(0, 0, W, H);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  function setTint(rgb: [number, number, number]) {
    cfg.tint = rgb;
  }

  resize();
  initStars();
  window.addEventListener("resize", resize);
  window.addEventListener("mousemove", onMouse);
  start();

  return { start, stop, destroy, setTint };
}
